import { QBox, QButton, QFormControl, QHeading, QIcon, QInput, QStack, QSwitch, QText } from '@qualio/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentTemplateDTO, SectionErrors } from '../../../types';
import i18n from '../../../i18n/config';
import { trimAndReplaceMultiSpaceWithOneSpace } from '../../../utils/textUtils';
import ContentBoxErrors from './ContentBoxErrors';
import { elementAtIndex } from '../../../utils/arrayUtils';

export type ContentBoxProps = {
  templateDto: ContentTemplateDTO;
  setTemplateDto: (templateDto: ContentTemplateDTO) => void;
  index: number;
  sectionErrors: SectionErrors[];
  setSectionErrors: (errors: SectionErrors[]) => void;
  sectionTitlesTouched: boolean[];
  setSectionTitlesTouched: (touched: boolean[]) => void;
  setPageError: (value: boolean) => void;
  resolutionSection: number | null;
};

type SectionTitleInputProps = {
  initialTitle: string;
  onTitleChange: (title: string) => void;
  onBlur: (title: string) => void;
  isInvalid: boolean;
  dataCy: string;
};

const SectionTitleInput = ({ initialTitle, onTitleChange, onBlur, isInvalid, dataCy }: SectionTitleInputProps) => {
  const [value, setValue] = useState(initialTitle);

  return (
    <QInput
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        onTitleChange(e.target.value);
      }}
      onBlur={(e) => {
        const trimmed = trimAndReplaceMultiSpaceWithOneSpace(e.target.value);
        setValue(trimmed);
        onBlur(trimmed);
      }}
      isInvalid={isInvalid}
      name="title"
      data-cy={dataCy}
    />
  );
};

const INVALID_TITLE_CHARS = '\'"';
const getResolutionSectionHelpText = (isResolutionSection: boolean, resolutionSectionExists: boolean) => {
  return !isResolutionSection && resolutionSectionExists
    ? i18n.t('templates.messages.onlyOneResolutionSectionAllowed')
    : i18n.t('templates.messages.resolutionSectionDescription');
};

const ContentBox = (props: ContentBoxProps) => {
  const { t } = useTranslation();
  const section = elementAtIndex(props.templateDto.sections, props.index);
  const resolutionSectionAllowed = props.templateDto.canHaveResolutionSection;
  const isResolutionSection = section.type === 'resolution';

  const deleteSection = () => {
    const template = { ...props.templateDto };
    template.sections.splice(props.index, 1);
    for (let i = props.index; i < template.sections.length; i++) {
      template.sections[i]!.position--;
    }
    props.setTemplateDto(template);

    const sectionErrors = [...props.sectionErrors];
    sectionErrors.splice(props.index, 1);
    props.setSectionErrors(sectionErrors);
    checkPageError(sectionErrors);

    const sectionTitlesTouched = [...props.sectionTitlesTouched];
    sectionTitlesTouched.splice(props.index, 1);
    props.setSectionTitlesTouched(sectionTitlesTouched);
  };

  const move = (sectionPos: number, direction: number) => {
    const template = { ...props.templateDto };
    const sectionToMove = elementAtIndex(template.sections, sectionPos);

    sectionToMove.position = sectionToMove.position + direction;

    const sectionAtTarget = elementAtIndex(template.sections, sectionPos + direction);

    template.sections[sectionPos] = sectionAtTarget;

    sectionAtTarget.position = elementAtIndex(template.sections, sectionPos).position - direction;

    template.sections[sectionPos + direction] = sectionToMove;
    props.setTemplateDto(template);

    const errors = [...props.sectionErrors];
    const errorToChange = elementAtIndex(errors, sectionPos + direction);

    errors[sectionPos + direction] = elementAtIndex(errors, sectionPos);
    errors[sectionPos] = errorToChange;
    props.setSectionErrors(errors);

    const sectionTitlesTouched = [...props.sectionTitlesTouched];
    const touchedToChange = elementAtIndex(sectionTitlesTouched, sectionPos + direction);

    sectionTitlesTouched[sectionPos + direction] = elementAtIndex(sectionTitlesTouched, sectionPos);
    sectionTitlesTouched[sectionPos] = touchedToChange;
    props.setSectionTitlesTouched(sectionTitlesTouched);
  };

  const moveUp = () => move(props.index, -1);
  const moveDown = () => move(props.index, 1);

  const checkPageError = (errors: SectionErrors[]) => {
    let pageHasError = false;

    for (const error of errors) {
      pageHasError = pageHasError || error.hasErrors();
    }

    if (!pageHasError) {
      props.setPageError(false);
    }
  };

  const handleTitleChange = (newTitle: string) => {
    const sections = props.templateDto.sections.map((s, i) => (i === props.index ? { ...s, title: newTitle } : s));
    props.setTemplateDto({ ...props.templateDto, sections });

    const errors = [...props.sectionErrors];
    elementAtIndex(errors, props.index).reset();
    props.setSectionErrors(errors);

    checkPageError(errors);
  };

  const handleTitleBlur = (title: string) => {
    const sections = props.templateDto.sections.map((s, i) => (i === props.index ? { ...s, title } : s));
    props.setTemplateDto({ ...props.templateDto, sections });

    if (title.trim() === '') {
      const errors = [...props.sectionErrors];
      elementAtIndex(errors, props.index).titleBlank = true;
      props.setSectionErrors(errors);
      props.setPageError(true);
    } else {
      for (const char of INVALID_TITLE_CHARS) {
        if (title.indexOf(char) !== -1) {
          const errors = [...props.sectionErrors];
          elementAtIndex(errors, props.index).invalidCharacters += char;
          props.setSectionErrors(errors);
          props.setPageError(true);
        }
      }
    }

    if (elementAtIndex(props.sectionErrors, props.index).duplicateTitle) {
      props.setPageError(true);
    }

    const sectionTitlesTouched = [...props.sectionTitlesTouched];
    sectionTitlesTouched[props.index] = true;
    props.setSectionTitlesTouched(sectionTitlesTouched);
  };

  return (
    <QBox
      mt="6"
      border={'1px'}
      borderColor={'gray.200'}
      rounded={'md'}
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
      overflow="hidden"
    >
      <QBox p={6}>
        <QBox pb={4}>
          <QHeading size="sm">{t('common.labels.sectionWithPosition', { position: section.position })}</QHeading>
        </QBox>
        <QFormControl>
          <QStack direction={['column']} spacing="4">
            <QBox>
              <QText>{t('common.labels.title')}</QText>
              <SectionTitleInput
                initialTitle={section.title}
                onTitleChange={handleTitleChange}
                onBlur={handleTitleBlur}
                isInvalid={elementAtIndex(props.sectionErrors, props.index).hasErrors()}
                dataCy={'title' + section.position}
              />
              <ContentBoxErrors sectionErrors={elementAtIndex(props.sectionErrors, props.index)} />
            </QBox>
            {resolutionSectionAllowed ? (
              <QFormControl
                size="sm"
                helper={getResolutionSectionHelpText(isResolutionSection, Boolean(props.resolutionSection))}
              >
                <QSwitch
                  isChecked={isResolutionSection}
                  isDisabled={isResolutionSection ? false : Boolean(props.resolutionSection)}
                  onChange={(event) => {
                    const isChecked = event.target.checked;

                    const template = { ...props.templateDto };
                    elementAtIndex(template.sections, props.index).type = isChecked ? 'resolution' : 'text';

                    props.setTemplateDto(template);
                  }}
                >
                  {t('templates.messages.makeThisAResolutionSection')}
                </QSwitch>
              </QFormControl>
            ) : null}
          </QStack>
        </QFormControl>
      </QBox>
      <QBox
        border={'1px'}
        borderColor={'gray.200'}
        backgroundColor={'gray.50'}
        py={2}
        px={4}
        display={'flex'}
        justifyContent={'space-between'}
        m={'-1'}
      >
        <QButton
          variant="ghost"
          leftIcon="Trash"
          isDisabled={props.templateDto.sections.length === 1}
          onClick={deleteSection}
          data-cy={'delete' + section.position}
        >
          {t('common.actions.delete')}
        </QButton>
        <QBox>
          <QButton
            variant="ghost"
            onClick={moveUp}
            isDisabled={props.index === 0}
            data-cy={'moveup' + section.position}
          >
            <QIcon iconName={'ArrowUp'} />
          </QButton>
          <QButton
            variant="ghost"
            onClick={moveDown}
            isDisabled={section.position === props.templateDto.sections.length}
            data-cy={'movedown' + section.position}
          >
            <QIcon iconName={'ArrowDown'} />
          </QButton>
        </QBox>
      </QBox>
    </QBox>
  );
};

export default ContentBox;
